import { roleAtLeast, type ApiProject, type ApiWorkspace } from '@tomo/core/api';
import { toast } from '@tomo/ui';
import type { NavigateFunction } from 'react-router';
import { api } from '../../data/cloud/api.ts';
import { addCloudProject, createSampleProject, getOpenSession, importProjectArchive } from '../../data/projects.ts';
import { patchRegistry } from '../../data/registry.ts';
import { pickFiles } from '../../lib/files.ts';

/** Pick a .tomo file, import it as a new project and open it. */
export async function importProjectFile(navigate: NavigateFunction) {
  const [file] = await pickFiles({ accept: '.tomo,.zip', multiple: false });
  if (!file) return;
  const id = toast({ title: `Importing ${file.name}…`, duration: 0 });
  try {
    const projectId = await importProjectArchive(file);
    toast.success('Project imported', { id });
    navigate(`/p/${projectId}`);
  } catch (e) {
    toast.error('Import failed', { id, description: e instanceof Error ? e.message : String(e) });
  }
}

/** Create a fresh copy of the Emberfall sample and open it. */
export async function openSampleProject(navigate: NavigateFunction) {
  try {
    navigate(`/p/${await createSampleProject()}`);
  } catch (e) {
    toast.error('Could not create the sample project', { description: String(e) });
  }
}

export type WorkspaceProjects = { workspace: ApiWorkspace; projects: ApiProject[] };

/** Every project in these cloud workspaces. A workspace that fails to load counts as empty. */
export function loadCloudProjects(workspaces: ApiWorkspace[]): Promise<WorkspaceProjects[]> {
  return Promise.all(
    workspaces.map((workspace) =>
      api.workspaces.projects(workspace.id).then(
        (projects) => ({ workspace, projects }),
        () => ({ workspace, projects: [] }),
      ),
    ),
  );
}

/** Open a cloud project, listing it on this device first; its content downloads as it opens. */
export async function openCloudProject(navigate: NavigateFunction, project: ApiProject, workspace: ApiWorkspace) {
  await addCloudProject(project, workspace);
  navigate(`/p/${project.id}`);
}

/** Workspaces this person can put projects in: Pro ones where they can edit. */
export const canHoldProjects = (workspace: ApiWorkspace) => workspace.plan === 'pro' && roleAtLeast(workspace.role, 'editor');

/**
 * Move a project from this device into a cloud workspace. The server registers it, then the
 * project's session connects and uploads the local copy.
 */
export async function moveProjectToWorkspace(project: { id: string; name: string; icon: string; color: string }, workspace: ApiWorkspace) {
  await api.workspaces.createProject(workspace.id, { id: project.id, name: project.name, icon: project.icon, color: project.color });
  await patchRegistry(project.id, { cloud: { workspaceId: workspace.id, role: workspace.role } });
  getOpenSession(project.id)?.refreshCloud();
}
